import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { compose } from "redux";
import { bindPromiseCreators } from "redux-saga-routines";

import { UrlProvider } from "../../../../../api/UrlProvider";
import { getStocksListResultUrlPattern } from "../../../../../lib/tvFullScreenQuery";
import withChartData from "../../../../../hocs/withChartData";
import withUserData from "../../../../../hocs/withUserData";
import useTvChartBridgeFrameMessaging from "../../../../../hooks/useTvChartBridgeFrameMessaging";
import buildTvChartBridgeChartActionHandlers from "../../../../../lib/TvChartBridge/buildTvChartBridgeChartActionHandlers";
import buildTvChartBridgeInitPayload, {
  buildTvChartBridgePermissions,
  buildTvChartBridgeTranslations
} from "../../../../../lib/TvChartBridge/buildTvChartBridgeInitPayload";
import createTvChartBridgeDataFeed from "../../../../../lib/TvChartBridge/createTvChartBridgeDataFeed";
import resolveTvChartBridgeSavedChart from "../../../../../lib/TvChartBridge/resolveTvChartBridgeSavedChart";
import buildTvChartBridgeMarkClickHandler from "../../../../../lib/TvChartBridge/buildTvChartBridgeMarkClickHandler";
import buildTvChartBridgeMarkersPayload from "../../../../../lib/TvChartBridge/buildTvChartBridgeMarkersPayload";
import applyTvChartStockChangeDataReset from "../../../../../lib/TvChartBridge/applyTvChartStockChangeDataReset";
import { getTranslatedMarket } from "../../../../../lib/getTranslatedMarket";
import { showModal } from "../../../../../lib/react-redux-modal-provider";
import {
  addChartTemplateRoutinePromiseCreator,
  fetchChartTemplateDetailsRoutinePromiseCreator,
  fetchChartTemplatesRoutine,
  removeChartTemplateRoutinePromiseCreator,
  updateChartTemplateRoutinePromiseCreator
} from "../../../../../redux/modules/chartTemplates";
import { getChartTemplatesState } from "../../../../../redux/modules/chartTemplates/selector";
import {
  addChartAnalysisRoutinePromiseCreator,
  clearChartAnalysis,
  deleteChartAnalysisRoutinePromiseCreator,
  fetchChartAnalysesDetailsRoutinePromiseCreator,
  fetchChartAnalysesRoutine,
  fetchChartAnalysesRoutinePromiseCreator,
  setChartAnalysisVisibilityRoutinePromiseCreator,
  updateChartAnalysisRoutinePromiseCreator
} from "../../../../../redux/modules/chartAnalyses";
import { getChartAnalysesState } from "../../../../../redux/modules/chartAnalyses/selector";
import { PLAN_PERMISSIONS_NAMES } from "../../../../../redux/modules/auth/userPermissions/selector";
import { getBackupsData } from "../../../../../redux/modules/tvchartData/selector";
import {
  getLocale,
  getMarketTranslations,
  getTranslations
} from "../../../../../redux/modules/translations/selector";
import DEFAULT_DRAWN_OBJECTS_OPTIONS from "../../../../../lib/TvChart/defaultDrawnObjectsOptions";
import Loader from "../../Loader";
import TVChartStocksList from "../../TVChartStocksList";
import TvChartBridgeFrame from "../components/TvChartBridgeFrame";

const WEB_LIBRARY_PATH = "/charting_library/";
const WEB_WIDGET_SCRIPT_URL = "/charting_library/charting_library.js";
const WEB_OVERLAYS_SCRIPT_URL = "/charting_library/tvchartbridge-overlays.js";

function getDefaultSaveSettings() {
  return DEFAULT_DRAWN_OBJECTS_OPTIONS.map(option => ({
    key: option.key,
    value: option.value
  }));
}

function TvChartBridgeView({
  stock,
  locale,
  translate,
  translationPhrases,
  latestQuoteDatetimeUtc,
  height = 500,
  userData,
  chartTemplatesState,
  chartAnalysesState,
  backups,
  fetchChartTemplates,
  fetchChartAnalyses,
  clearChartAnalysis: clearChartAnalysisAction,
  tvChartRoutinePromiseCreator,
  tvChartGetUserDataRoutinePromiseCreator,
  tvChartUserDataRoutinePromiseCreator,
  addChartTemplateAsync,
  updateChartTemplateAsync,
  removeChartTemplateAsync,
  fetchChartTemplateDetailsAsync,
  fetchChartAnalysesAsync,
  addChartAnalysisAsync,
  updateChartAnalysisAsync,
  setChartAnalysisVisibilityAsync,
  removeChartAnalysisAsync,
  fetchChartAnalysisDetailsAsync,
  hasPermission,
  userID,
  setBackups: setBackupsProp,
  onRefreshStarted,
  onRefreshEnded,
  dividends,
  reportDates,
  mediaMonitorMessages,
  lastMinMaxPoints,
  insiderTransactions,
  priceAlerts,
  refreshReportDates,
  refreshDividends,
  refreshPriceAlerts,
  refreshInsiderTransactions,
  resetChartMarkerData,
  showStocksListButton = false,
  showFullscreenButton = false,
  showLeadingFullscreenButton = false,
  isFullscreenView = false,
  onOpenFullscreenChart: onOpenFullscreenChartProp,
  history,
  marketTranslations
}) {
  const [initPayload, setInitPayload] = useState(null);
  const [dataFeed, setDataFeed] = useState(null);
  const [stocksListVisible, setStocksListVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drawnObjectsOptions, setDrawnObjectsOptions] = useState(
    DEFAULT_DRAWN_OBJECTS_OPTIONS
  );
  const chartTemplatesStateRef = useRef(chartTemplatesState);
  const chartAnalysesStateRef = useRef(chartAnalysesState);
  const backupsRef = useRef(backups);
  const userDataRef = useRef(userData);

  chartTemplatesStateRef.current = chartTemplatesState;
  chartAnalysesStateRef.current = chartAnalysesState;
  backupsRef.current = backups;
  userDataRef.current = userData;

  useEffect(() => {
    fetchChartTemplates?.();
  }, [fetchChartTemplates]);

  useEffect(() => {
    if (!stock?.id) {
      return;
    }

    clearChartAnalysisAction?.();
    fetchChartAnalyses?.({ stockId: stock.id });
  }, [stock?.id, clearChartAnalysisAction, fetchChartAnalyses]);

  useEffect(() => {
    applyTvChartStockChangeDataReset({
      stockId: stock?.id,
      resetChartMarkerData,
      refreshReportDates,
      refreshDividends,
      refreshPriceAlerts,
      refreshInsiderTransactions
    });
  }, [stock?.id]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!stock?.id) {
        return;
      }

      if (chartTemplatesState?.list === null) {
        return;
      }

      setLoading(true);

      const feed = createTvChartBridgeDataFeed({
        stock,
        tvChartRoutinePromiseCreator,
        translate,
        latestQuoteDatetimeUtc
      });

      let chartUserData = null;

      try {
        chartUserData = await tvChartGetUserDataRoutinePromiseCreator(stock.id);
      } catch (error) {
        chartUserData = null;
      }

      if (cancelled) {
        return;
      }

      const resolved = resolveTvChartBridgeSavedChart({
        chartUserData,
        chartTemplatesState,
        hasPermission
      });

      setDrawnObjectsOptions(resolved.drawnObjectsOptions);
      if (resolved.backups?.length && setBackupsProp) {
        setBackupsProp(resolved.backups);
      }

      setDataFeed(feed);
      setInitPayload(
        buildTvChartBridgeInitPayload({
          stock,
          locale,
          libraryPath: WEB_LIBRARY_PATH,
          widgetScriptUrl: WEB_WIDGET_SCRIPT_URL,
          overlaysScriptUrl: WEB_OVERLAYS_SCRIPT_URL,
          savedChart: resolved.savedChart,
          drawnObjectsOptions: resolved.drawnObjectsOptions,
          backups: resolved.backups?.length
            ? resolved.backups
            : backupsRef.current || [],
          permissions: buildTvChartBridgePermissions(hasPermission, userID),
          translations: buildTvChartBridgeTranslations(
            translate,
            translationPhrases
          ),
          phrases: translationPhrases,
          isNative: false,
          showStocksListButton,
          showFullscreenButton,
          showLeadingFullscreenButton
        })
      );
      setLoading(false);
      // Do not call onRefreshStarted here — StockPageTVChart replaces this
      // component with a Loader when loading=true, which would cancel bootstrap.
      onRefreshEnded?.();
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [stock?.id, chartTemplatesState?.list, latestQuoteDatetimeUtc, showStocksListButton, showFullscreenButton, showLeadingFullscreenButton]);

  const chartActionHandlers = useMemo(
    () =>
      buildTvChartBridgeChartActionHandlers({
        stockId: stock?.id,
        getChartTemplatesState: () => chartTemplatesStateRef.current,
        getChartAnalysesState: () => chartAnalysesStateRef.current,
        getBackups: () => backupsRef.current || [],
        addChartTemplateAsync,
        updateChartTemplateAsync,
        removeChartTemplateAsync,
        fetchChartTemplateDetailsAsync,
        fetchChartAnalysesAsync,
        addChartAnalysisAsync,
        updateChartAnalysisAsync,
        setChartAnalysisVisibilityAsync,
        removeChartAnalysisAsync,
        fetchChartAnalysisDetailsAsync
      }),
    [
      stock?.id,
      addChartTemplateAsync,
      updateChartTemplateAsync,
      removeChartTemplateAsync,
      fetchChartTemplateDetailsAsync,
      fetchChartAnalysesAsync,
      addChartAnalysisAsync,
      updateChartAnalysisAsync,
      setChartAnalysisVisibilityAsync,
      removeChartAnalysisAsync,
      fetchChartAnalysisDetailsAsync
    ]
  );

  const markersPayload = useMemo(
    () =>
      buildTvChartBridgeMarkersPayload({
        dividends,
        reportDates,
        mediaMonitorMessages,
        lastMinMaxPoints,
        insiderTransactions
      }),
    [
      dividends,
      reportDates,
      mediaMonitorMessages,
      lastMinMaxPoints,
      insiderTransactions
    ]
  );

  const handleMarkClick = useMemo(
    () =>
      buildTvChartBridgeMarkClickHandler({
        showModal,
        hasPermission,
        translate,
        getUserData: () => userDataRef.current
      }),
    [hasPermission, translate]
  );

  const handleOpenPriceAlert = useCallback(
    ({ price } = {}) => {
      if (!stock?.id) {
        return;
      }

      showModal("NewPriceAlertsModal", {
        stockId: stock.id,
        price
      });
    },
    [stock?.id]
  );

  const handleOpenStocksList = useCallback(() => {
    setStocksListVisible(true);
  }, []);

  const handleOpenFullscreenChart = useCallback(() => {
    if (onOpenFullscreenChartProp) {
      onOpenFullscreenChartProp();
      return;
    }

    if (!stock?.ticker || !stock?.market || !locale) {
      return;
    }

    const marketId = getTranslatedMarket(
      locale,
      stock.market,
      marketTranslations
    );
    const stockId = stock.ticker.toLowerCase();

    let url;

    if (isFullscreenView) {
      const search = window.location.search || "";
      const returnToMatch = search.match(/[?&]return_to=([^&]*)/);
      const returnTo = returnToMatch
        ? decodeURIComponent(returnToMatch[1])
        : null;

      url =
        returnTo ||
        UrlProvider.getUrl("fe.stockPage", {
          locale,
          marketId,
          stockId
        });
    } else {
      url = UrlProvider.getUrl(getStocksListResultUrlPattern(true), {
        locale,
        marketId,
        stockId
      });
    }

    if (history?.push) {
      history.push(url);
      return;
    }

    window.location.assign(url);
  }, [
    history,
    isFullscreenView,
    locale,
    marketTranslations,
    onOpenFullscreenChartProp,
    stock?.market,
    stock?.ticker
  ]);

  const handleSaveChart = useCallback(
    ({ chart, settings } = {}) => {
      if (!chart) {
        return;
      }

      if (
        typeof hasPermission === "function" &&
        !hasPermission([PLAN_PERMISSIONS_NAMES.TRADING_VIEW_CHART])
      ) {
        return;
      }

      const nextSettings =
        Array.isArray(settings) && settings.length
          ? settings
          : drawnObjectsOptions.map(option => ({
              key: option.key,
              value: option.value
            }));

      if (Array.isArray(settings) && settings.length) {
        setDrawnObjectsOptions(prev => {
          let changed = false;
          const next = prev.map(option => {
            const saved = settings.find(item => item.key === option.key);
            if (!saved || saved.value === option.value) {
              return option;
            }
            changed = true;
            return { ...option, value: saved.value };
          });
          return changed ? next : prev;
        });
      }

      tvChartUserDataRoutinePromiseCreator({
        stockId: stock.id,
        chart: JSON.stringify(chart),
        additionalData: {
          settings: nextSettings.length ? nextSettings : getDefaultSaveSettings()
        }
      })
        .then(data => {
          if (setBackupsProp) {
            setBackupsProp(data?.backups || []);
          }
        })
        .catch(error => {
          console.error("TvChartBridge: autosave failed", error);
        });
    },
    [
      drawnObjectsOptions,
      hasPermission,
      setBackupsProp,
      stock?.id,
      tvChartUserDataRoutinePromiseCreator
    ]
  );

  const stableInitPayload = useMemo(() => initPayload, [initPayload]);

  const { frameRef, onFrameLoad } = useTvChartBridgeFrameMessaging({
    initPayload: stableInitPayload,
    dataFeed,
    chartActionHandlers,
    onSaveChart: handleSaveChart,
    onMarkClick: handleMarkClick,
    onOpenPriceAlert: handleOpenPriceAlert,
    onOpenStocksList: handleOpenStocksList,
    onOpenFullscreenChart: handleOpenFullscreenChart,
    backups,
    markersPayload,
    drawnObjectsOptions,
    priceAlerts,
    stockCurrency: stock?.currency
  });

  if (loading || !initPayload || chartTemplatesState?.list === null) {
    return <Loader className="StockPageTVChart__loader" />;
  }

  return (
    <div className="tv-chart-wrapper" style={{ position: "relative", height }}>
      <TvChartBridgeFrame
        key={stock?.id}
        frameRef={frameRef}
        height={height}
        onFrameLoad={onFrameLoad}
      />
      {stocksListVisible ? (
        <TVChartStocksList
          close={() => setStocksListVisible(false)}
          resultUrlPattern={getStocksListResultUrlPattern(isFullscreenView)}
        />
      ) : null}
    </div>
  );
}

TvChartBridgeView.propTypes = {
  stock: PropTypes.object.isRequired,
  locale: PropTypes.string,
  translate: PropTypes.func,
  translationPhrases: PropTypes.object,
  latestQuoteDatetimeUtc: PropTypes.string,
  height: PropTypes.number,
  userData: PropTypes.object,
  chartTemplatesState: PropTypes.object,
  chartAnalysesState: PropTypes.object,
  backups: PropTypes.array,
  fetchChartTemplates: PropTypes.func,
  fetchChartAnalyses: PropTypes.func,
  clearChartAnalysis: PropTypes.func,
  tvChartRoutinePromiseCreator: PropTypes.func.isRequired,
  tvChartGetUserDataRoutinePromiseCreator: PropTypes.func.isRequired,
  tvChartUserDataRoutinePromiseCreator: PropTypes.func.isRequired,
  addChartTemplateAsync: PropTypes.func,
  updateChartTemplateAsync: PropTypes.func,
  removeChartTemplateAsync: PropTypes.func,
  fetchChartTemplateDetailsAsync: PropTypes.func,
  fetchChartAnalysesAsync: PropTypes.func,
  addChartAnalysisAsync: PropTypes.func,
  updateChartAnalysisAsync: PropTypes.func,
  setChartAnalysisVisibilityAsync: PropTypes.func,
  removeChartAnalysisAsync: PropTypes.func,
  fetchChartAnalysisDetailsAsync: PropTypes.func,
  hasPermission: PropTypes.func,
  userID: PropTypes.number,
  setBackups: PropTypes.func,
  onRefreshStarted: PropTypes.func,
  onRefreshEnded: PropTypes.func,
  dividends: PropTypes.array,
  reportDates: PropTypes.array,
  mediaMonitorMessages: PropTypes.array,
  lastMinMaxPoints: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  insiderTransactions: PropTypes.array,
  priceAlerts: PropTypes.array,
  refreshReportDates: PropTypes.func,
  refreshDividends: PropTypes.func,
  refreshPriceAlerts: PropTypes.func,
  refreshInsiderTransactions: PropTypes.func,
  resetChartMarkerData: PropTypes.func,
  showStocksListButton: PropTypes.bool,
  showFullscreenButton: PropTypes.bool,
  showLeadingFullscreenButton: PropTypes.bool,
  isFullscreenView: PropTypes.bool,
  onOpenFullscreenChart: PropTypes.func,
  history: PropTypes.object,
  marketTranslations: PropTypes.object
};

const mapStateToProps = state => {
  const locale = getLocale(state);
  const allTranslations = getTranslations(state) || {};

  return {
    chartTemplatesState: getChartTemplatesState(state),
    chartAnalysesState: getChartAnalysesState(state),
    backups: getBackupsData(state),
    locale,
    translationPhrases: allTranslations[locale] || {},
    marketTranslations: getMarketTranslations(state)
  };
};

const mapDispatchToProps = {
  fetchChartTemplates: fetchChartTemplatesRoutine,
  fetchChartAnalyses: fetchChartAnalysesRoutine,
  clearChartAnalysis
};

const mapPromiseCreatorsToProps = dispatch =>
  bindPromiseCreators(
    {
      addChartTemplateAsync: addChartTemplateRoutinePromiseCreator,
      updateChartTemplateAsync: updateChartTemplateRoutinePromiseCreator,
      removeChartTemplateAsync: removeChartTemplateRoutinePromiseCreator,
      fetchChartTemplateDetailsAsync: fetchChartTemplateDetailsRoutinePromiseCreator,
      fetchChartAnalysesAsync: fetchChartAnalysesRoutinePromiseCreator,
      addChartAnalysisAsync: addChartAnalysisRoutinePromiseCreator,
      updateChartAnalysisAsync: updateChartAnalysisRoutinePromiseCreator,
      setChartAnalysisVisibilityAsync: setChartAnalysisVisibilityRoutinePromiseCreator,
      removeChartAnalysisAsync: deleteChartAnalysisRoutinePromiseCreator,
      fetchChartAnalysisDetailsAsync: fetchChartAnalysesDetailsRoutinePromiseCreator
    },
    dispatch
  );

export default compose(
  withRouter,
  withUserData,
  withChartData,
  connect(mapStateToProps, mapDispatchToProps),
  connect(null, mapPromiseCreatorsToProps)
)(TvChartBridgeView);
